/**
 * ClaimReportButtons
 *
 * Two tiny buttons ("Claim this business" + "Report / Remove") for use
 * anywhere we show business details (map popup, business card, detail page).
 *
 * Opens a minimal Radix Dialog with a short form that posts to:
 *   - POST /api/verification/submit   (Claim)
 *   - POST /api/optout/submit         (Report/Remove)
 *
 * Anti-spam:
 *   - honeypot `company_website` field (invisible to users)
 *   - relies on backend per-IP rate limit
 *
 * i18n: ET + EN. FI/RU fall back to EN.
 * No new deps — uses existing Dialog, Input, Button, Sonner toast.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  et: {
    claimBtn: 'Kinnita ettevõte',
    reportBtn: 'Teata / Eemalda',
    claimTitle: 'Kinnita see ettevõte',
    claimDesc: 'Kui sa oled omanik või esindaja, esita lühike taotlus. Admin vaatab üle.',
    reportTitle: 'Teata / Eemalda',
    reportDesc: 'Palun kirjelda lühidalt, mis on valesti. Me vaatame iga teate üle.',
    name: 'Nimi',
    email: 'E-post',
    phone: 'Telefon (valikuline)',
    relationship: 'Roll',
    ownerOpt: 'Omanik',
    employeeOpt: 'Töötaja',
    repOpt: 'Esindaja',
    message: 'Sõnum (valikuline)',
    reason: 'Põhjus',
    reasonWrong: 'Vale info',
    reasonNotMine: 'See pole minu oma',
    reasonDup: 'Duplikaat',
    reasonClosed: 'Ettevõte suletud',
    reasonPriv: 'Privaatsus',
    reasonOther: 'Muu',
    details: 'Täpsustus (valikuline)',
    ack: 'Mõistan, et valesid teateid võib ignoreerida.',
    submit: 'Saada',
    cancel: 'Tühista',
    thanks: 'Täname — taotlus on saadetud.',
    fail: 'Saatmine ebaõnnestus',
    tooMany: 'Liiga palju taotlusi. Proovi hiljem uuesti.',
  },
  en: {
    claimBtn: 'Claim this business',
    reportBtn: 'Report / Remove',
    claimTitle: 'Claim this business',
    claimDesc: 'If you are the owner or representative, submit a short request. Admins will review.',
    reportTitle: 'Report / Remove',
    reportDesc: 'Briefly describe the issue. Every report is reviewed.',
    name: 'Name',
    email: 'Email',
    phone: 'Phone (optional)',
    relationship: 'Role',
    ownerOpt: 'Owner',
    employeeOpt: 'Employee',
    repOpt: 'Representative',
    message: 'Message (optional)',
    reason: 'Reason',
    reasonWrong: 'Wrong info',
    reasonNotMine: 'Not mine',
    reasonDup: 'Duplicate',
    reasonClosed: 'Business closed',
    reasonPriv: 'Privacy',
    reasonOther: 'Other',
    details: 'Details (optional)',
    ack: 'I understand false reports may be ignored.',
    submit: 'Submit',
    cancel: 'Cancel',
    thanks: 'Thanks — request submitted.',
    fail: 'Submission failed',
    tooMany: 'Too many requests. Please try again later.',
  },
};
const useT = () => {
  const { language } = useLanguage();
  return T[language] || T.en;
};

// ---------- CLAIM DIALOG ----------
const ClaimDialog = ({ open, onOpenChange, business, l }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', relationship: 'owner', message: '', company_website: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/verification/submit`, {
        ...form,
        business_id: business?.id,
        business_slug: business?.slug,
        registry_id: business?.registry_id,
        company_name_snapshot: business?.name || business?.title,
        country: business?.country,
      });
      toast.success(l.thanks);
      onOpenChange(false);
      setForm({ name: '', email: '', phone: '', relationship: 'owner', message: '', company_website: '' });
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 429 ? l.tooMany : l.fail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> {l.claimTitle}
          </DialogTitle>
          <DialogDescription>{l.claimDesc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {/* Honeypot — visually hidden but focusable by naive bots */}
          <input
            type="text"
            name="company_website"
            value={form.company_website}
            onChange={(e) => setForm((f) => ({ ...f, company_website: e.target.value }))}
            tabIndex="-1"
            autoComplete="off"
            style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />
          <Input required placeholder={l.name} value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="claim-name" />
          <Input required type="email" placeholder={l.email} value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            data-testid="claim-email" />
          <Input placeholder={l.phone} value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            data-testid="claim-phone" />
          <select
            value={form.relationship}
            onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
            className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
            data-testid="claim-relationship"
          >
            <option value="owner">{l.ownerOpt}</option>
            <option value="employee">{l.employeeOpt}</option>
            <option value="representative">{l.repOpt}</option>
          </select>
          <textarea
            placeholder={l.message}
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            maxLength={500}
            className="w-full border border-slate-300 rounded-md p-2 text-sm min-h-[64px]"
            data-testid="claim-message"
          />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{l.cancel}</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="claim-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : l.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ---------- REPORT DIALOG ----------
const ReportDialog = ({ open, onOpenChange, business, l }) => {
  const [form, setForm] = useState({
    email: '', reason: 'wrong_info', details: '', acknowledged: true, company_website: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/optout/submit`, {
        ...form,
        business_id: business?.id,
        business_slug: business?.slug,
        company_name_snapshot: business?.name || business?.title,
      });
      toast.success(l.thanks);
      onOpenChange(false);
      setForm({ email: '', reason: 'wrong_info', details: '', acknowledged: true, company_website: '' });
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 429 ? l.tooMany : l.fail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-600" /> {l.reportTitle}
          </DialogTitle>
          <DialogDescription>{l.reportDesc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="text"
            name="company_website"
            value={form.company_website}
            onChange={(e) => setForm((f) => ({ ...f, company_website: e.target.value }))}
            tabIndex="-1"
            autoComplete="off"
            style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />
          <Input required type="email" placeholder={l.email} value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            data-testid="report-email" />
          <select
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
            data-testid="report-reason"
          >
            <option value="wrong_info">{l.reasonWrong}</option>
            <option value="not_mine">{l.reasonNotMine}</option>
            <option value="duplicate">{l.reasonDup}</option>
            <option value="closed">{l.reasonClosed}</option>
            <option value="privacy">{l.reasonPriv}</option>
            <option value="other">{l.reasonOther}</option>
          </select>
          <textarea
            placeholder={l.details}
            value={form.details}
            onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
            maxLength={500}
            className="w-full border border-slate-300 rounded-md p-2 text-sm min-h-[64px]"
            data-testid="report-details"
          />
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={form.acknowledged}
              onChange={(e) => setForm((f) => ({ ...f, acknowledged: e.target.checked }))}
              data-testid="report-ack"
              className="mt-0.5"
            />
            {l.ack}
          </label>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{l.cancel}</Button>
            <Button type="submit" disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="report-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : l.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ClaimReportLinks
 * Small subtle text links — drop into any business popup / card.
 *
 * Props:
 *   business: { id, slug, name, title, registry_id, country, status }
 *   className: optional container class
 */
export const ClaimReportLinks = ({ business, className = '' }) => {
  const l = useT();
  const [claimOpen, setClaimOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <div className={`flex items-center gap-3 text-xs ${className}`}>
      <button
        type="button"
        onClick={() => setClaimOpen(true)}
        className="text-emerald-700 hover:text-emerald-800 hover:underline flex items-center gap-1"
        data-testid="claim-link"
      >
        <ShieldCheck className="h-3 w-3" /> {l.claimBtn}
      </button>
      <span className="text-slate-300">·</span>
      <button
        type="button"
        onClick={() => setReportOpen(true)}
        className="text-slate-500 hover:text-rose-600 hover:underline flex items-center gap-1"
        data-testid="report-link"
      >
        <AlertTriangle className="h-3 w-3" /> {l.reportBtn}
      </button>
      <ClaimDialog open={claimOpen} onOpenChange={setClaimOpen} business={business} l={l} />
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} business={business} l={l} />
    </div>
  );
};

export default ClaimReportLinks;
